import React from "react";
import { Link } from "react-router-dom";
import { Heart, ShoppingCart, Trash2 } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { useWishlist } from "../context/WishlistContext";
import { Toast } from "../components/Toast";

export default function WishlistPage() {
  const { user } = useAuth();
  const { items, toggleSaved } = useWishlist();
  const { addToCart } = useCart();
  const [toast, setToast] = React.useState(null);

  if (!user) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center" data-testid="wishlist-login-required">
        <Helmet><title>Saved Items | Carepoint Health Supplies</title></Helmet>
        <Heart className="w-12 h-12 text-rose-400 mx-auto mb-4" />
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Sign in to view your saved items</h1>
        <p className="text-gray-600 mb-6">Your wishlist is saved to your account so you can pick up where you left off on any device.</p>
        <Link to="/login" className="bg-[#00a99d] hover:bg-[#008c82] text-white px-6 py-3 rounded-lg font-semibold inline-block">
          Sign in
        </Link>
      </div>
    );
  }

  const handleAdd = async (productId) => {
    const ok = await addToCart(productId);
    setToast({ message: ok ? "Added to cart!" : "Could not add to cart", type: ok ? "success" : "error" });
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-10" data-testid="wishlist-page">
      <Helmet><title>Saved Items | Carepoint Health Supplies</title></Helmet>
      <div className="flex items-center gap-3 mb-6">
        <Heart className="w-7 h-7 text-rose-500 fill-rose-500" />
        <h1 className="text-3xl font-bold text-gray-900">Saved Items</h1>
        <span className="text-gray-500" data-testid="wishlist-count">({items.length})</span>
      </div>

      {items.length === 0 ? (
        <div className="bg-white border rounded-xl p-12 text-center shadow-sm" data-testid="wishlist-empty">
          <Heart className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <h2 className="text-xl font-semibold text-gray-900 mb-1">Nothing saved yet</h2>
          <p className="text-gray-600 mb-5">Tap the heart on any product to keep it here for later.</p>
          <Link to="/products" className="bg-[#00a99d] hover:bg-[#008c82] text-white px-5 py-2.5 rounded-lg font-semibold inline-block">
            Browse products
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((p) => {
            const stock = typeof p.stock === "number" ? p.stock : 0;
            const outOfStock = stock <= 0;
            return (
              <div key={p.id} className="bg-white rounded-xl border shadow-sm overflow-hidden flex flex-col" data-testid={`wishlist-card-${p.id}`}>
                <Link to={`/products/${p.id}`} className="block aspect-square bg-gray-50">
                  <img
                    src={p.image_url || "https://via.placeholder.com/400"}
                    alt={p.name}
                    className="w-full h-full object-contain"
                  />
                </Link>
                <div className="p-4 flex flex-col flex-1">
                  {p.brand && (
                    <span className="text-xs font-semibold text-[#00a99d] uppercase tracking-wide">{p.brand}</span>
                  )}
                  <Link to={`/products/${p.id}`} className="block">
                    <h3 className="font-semibold text-gray-800 mt-1 line-clamp-2 hover:text-[#00a99d]">{p.name}</h3>
                  </Link>
                  <p className="text-xs text-gray-500 mt-1">{p.sku}</p>
                  <div className="mt-2 flex items-baseline gap-2">
                    <span className="text-xl font-bold text-[#00a99d]">£{p.price.toFixed(2)}</span>
                    {p.original_price && (
                      <span className="text-sm text-gray-400 line-through">£{p.original_price.toFixed(2)}</span>
                    )}
                  </div>
                  <div className="mt-auto pt-4 flex gap-2">
                    <button
                      onClick={() => handleAdd(p.id)}
                      disabled={outOfStock}
                      className={`flex-1 inline-flex items-center justify-center gap-2 bg-[#00a99d] hover:bg-[#008c82] text-white font-semibold px-3 py-2 rounded-lg transition ${outOfStock ? "opacity-50 cursor-not-allowed" : ""}`}
                      data-testid={`wishlist-add-cart-${p.id}`}
                    >
                      <ShoppingCart className="w-4 h-4" />
                      {outOfStock ? "Out of stock" : "Add to cart"}
                    </button>
                    <button
                      onClick={() => toggleSaved(p.id)}
                      className="inline-flex items-center justify-center w-10 h-10 rounded-lg border text-gray-500 hover:border-rose-400 hover:text-rose-500 transition"
                      title="Remove from wishlist"
                      data-testid={`wishlist-remove-${p.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}
